import { useCallback, useMemo, useState } from 'react';
import { getAdHocGroupKey, type AdHocGroupConfig } from '../filtering';
import type { NewReport } from '../hooks/useNewReport';
import type { AnswerGroup } from '../types';
import { localizedText, type ReportComparisonGroup } from './types';

export function comparisonConfig(
  group: Extract<ReportComparisonGroup, { type: 'custom' }>,
  language: string,
): AdHocGroupConfig {
  return {
    type: 'byAnswer',
    label: localizedText(group.label, language),
    teamIds: new Set(group.teamIds),
    answerSelections: group.answerSelections,
  };
}
export function comparisonId(group: ReportComparisonGroup): string {
  return group.type === 'baseline' ? group.id : `byAnswer:${getAdHocGroupKey(comparisonConfig(group, ''))}`;
}

export function serializeComparisons(
  ids: ReadonlySet<string>,
  configs: Map<string, AdHocGroupConfig>,
  language: string,
  original: ReportComparisonGroup[] = [],
): ReportComparisonGroup[] {
  return [...ids].flatMap((id): ReportComparisonGroup[] => {
    const config = [...configs].find(([key]) => `byAnswer:${key}` === id)?.[1];
    if (!config) return [{ type: 'baseline', id }];
    const previous = original.find((group) => comparisonId(group) === id);
    return [
      {
        type: 'custom',
        label:
          previous?.type === 'custom' && localizedText(previous.label, language) === config.label
            ? previous.label
            : { ...(previous?.type === 'custom' ? previous.label : {}), [language]: config.label ?? '' },
        teamIds: [...(config.teamIds ?? [])],
        answerSelections: config.answerSelections.map((selection) => ({ ...selection })),
      },
    ];
  });
}

/** A modal-local copy of group rules: Cancel never changes the playground or any saved definition. */
export function useTableReport(
  report: NewReport,
  groups: ReportComparisonGroup[] | undefined,
  language: string,
): NewReport {
  const { getAnswerGroup, selectComparisonGroups } = report;
  const [configs, setConfigs] = useState(() => {
    const initial = new Map(report.adHocGroupConfigs);
    groups?.forEach((group) => {
      if (group.type === 'custom') {
        const config = comparisonConfig(group, language);
        initial.set(getAdHocGroupKey(config), config);
      }
    });
    return initial;
  });
  const adHocGroups = useMemo(() => {
    const result = new Map<string, AnswerGroup>();
    configs.forEach((config, key) => {
      const group = getAnswerGroup(config);
      if (group) result.set(key, group);
    });
    return result;
  }, [configs, getAnswerGroup]);
  const addAnswerGroup = useCallback(
    (config: AdHocGroupConfig) => {
      const group = getAnswerGroup(config);
      if (group)
        setConfigs((previous) =>
          new Map(previous).set(getAdHocGroupKey(config), {
            ...config,
            teamIds: new Set(config.teamIds),
            answerSelections: config.answerSelections.map((selection) => ({ ...selection })),
          }),
        );
      return group;
    },
    [getAnswerGroup],
  );
  const removeAnswerGroup = useCallback(
    (key: string) =>
      setConfigs((previous) => {
        const next = new Map(previous);
        next.delete(key);
        return next;
      }),
    [],
  );
  const selectGroups = useCallback(
    (ids: ReadonlySet<string>) =>
      [...ids].flatMap((id) => {
        const saved = [...adHocGroups.values()].find((group) => group.id === id);
        return saved ? [saved] : selectComparisonGroups(new Set([id]));
      }),
    [adHocGroups, selectComparisonGroups],
  );
  return {
    ...report,
    adHocGroups,
    adHocGroupConfigs: configs,
    addAnswerGroup,
    removeAnswerGroup,
    selectComparisonGroups: selectGroups,
  };
}
